import { useState } from "react";
import styled from "styled-components";
import { Button, Input, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";

export interface PostComment {
  commentId: number;
  content: string;
  name: string;
  createdAt?: string;
  modifiedAt?: string;
}

const formatDateTime = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
};

interface Props {
  comments: PostComment[];
  currentUser?: string | null;
  readOnly?: boolean; // 협력 사이트 게스트 등 - 작성 영역 숨김
  onAdd: (content: string) => Promise<void> | void;
  onEdit: (commentId: number, content: string) => Promise<void> | void;
  onDelete: (commentId: number) => Promise<void> | void;
}

// 자문 페이지(ConsultThread)와 동일한 UI 의 Post 댓글 스레드.
// - 위에서부터 누적된 댓글 목록, 그 아래에 작성 입력칸.
const CommentThread = ({ comments, currentUser, readOnly = false, onAdd, onEdit, onDelete }: Props) => {
  const [newText, setNewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  const handleAdd = async () => {
    const content = newText.trim();
    if (!content || submitting) return;
    setSubmitting(true);
    try {
      await onAdd(content);
      setNewText("");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (c: PostComment) => {
    setEditingId(c.commentId);
    setEditingText(c.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const submitEdit = async (commentId: number) => {
    const content = editingText.trim();
    if (!content) return;
    await onEdit(commentId, content);
    cancelEdit();
  };

  return (
    <Wrapper>
      <SectionTitle>Comment</SectionTitle>

      <List>
        {comments.map((c) => {
          const isOwn = !!currentUser && c.name === currentUser;
          const isEditing = editingId === c.commentId;
          return (
            <Item key={c.commentId}>
              <ItemHead>
                <Author>{c.name || "익명"}</Author>
                <HeadRight>
                  {c.createdAt && (
                    <DateText>
                      {formatDateTime(c.createdAt)}
                      {c.modifiedAt && c.modifiedAt !== c.createdAt ? " (수정됨)" : ""}
                    </DateText>
                  )}
                  {isOwn && !isEditing && (
                    <Actions>
                      <IconButton type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(c)} />
                      <Popconfirm
                        title="이 댓글을 삭제하시겠습니까?"
                        okText="삭제"
                        cancelText="취소"
                        onConfirm={() => onDelete(c.commentId)}
                      >
                        <IconButton type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Actions>
                  )}
                </HeadRight>
              </ItemHead>

              {isEditing ? (
                <EditArea>
                  <Input.TextArea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    autoSize={{ minRows: 3, maxRows: 10 }}
                  />
                  <EditActions>
                    <Button size="small" onClick={cancelEdit}>
                      취소
                    </Button>
                    <Button size="small" type="primary" onClick={() => submitEdit(c.commentId)} disabled={!editingText.trim()}>
                      저장
                    </Button>
                  </EditActions>
                </EditArea>
              ) : (
                <Content>{c.content}</Content>
              )}
            </Item>
          );
        })}
      </List>

      {/* 작성 영역 (free text) — 누적된 댓글 블록 아래에 위치 */}
      {!readOnly && (
        <Composer>
          <Input.TextArea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="댓글 내용을 입력하세요."
            autoSize={{ minRows: 3, maxRows: 8 }}
          />
          <ComposerActions>
            <Button type="primary" onClick={handleAdd} loading={submitting} disabled={!newText.trim()}>
              댓글 등록
            </Button>
          </ComposerActions>
        </Composer>
      )}
    </Wrapper>
  );
};

export default CommentThread;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #8c8c8c;
  margin: 4px 0 4px;
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const ComposerActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.div`
  border: 1px solid #ececec;
  border-radius: 10px;
  background: #fafafa;
  padding: 12px 14px;
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const Author = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e2e3a;
`;

const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateText = styled.span`
  font-size: 0.75rem;
  color: #9b9a97;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
`;

const IconButton = styled(Button)`
  color: #8c8c8c;
`;

const Content = styled.div`
  font-size: 0.92rem;
  color: #262626;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EditArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;
